import React from "react";
import { TRoadblockEvidenceFact, TRoadblockEvidenceLink } from "../../../../../domain/codecs/form/RoadblockForm";
import { CRMSpacing } from "../../../models/CRMSpacing";
import { CRMColors } from "../../../models/CRMColors";
import { SpacingColumn } from "../../BuildingBlocks/SpacingColumn";
import { SpacingRow } from "../../BuildingBlocks/SpacingRow";
import { Background } from "../../BuildingBlocks/Background";
import { CRMIcon } from "../../CRMIcon/CRMIcon";
import { CRMParagraph } from "../../Simple/CRMParagraph/CRMParagraph";
import { WeightSemiBold } from "../../WeightSemiBold/WeightSemiBold";
import { THexColor } from "../../../models/StringLiterals";

const factStatusIcon = (status: TRoadblockEvidenceFact["status"]): { iconName: "done" | "close" | "dot"; colour: THexColor } =>
    status === "present" ? { iconName: "done", colour: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_2 }
    : status === "missing" ? { iconName: "close", colour: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0 }
    : { iconName: "dot", colour: CRMColors.NEUTRAL_4 };

// A neutral row is a fact the item accepted without the thing being present — an
// unobtainable deed, an absent estate agent. "All confirmed" would read as a claim
// that it IS present, so any neutral row demotes the summary to "Details".
const summarise = (evidence: Array<TRoadblockEvidenceFact>): { variant: "outstanding" | "confirmed" | "neutral"; text: string } => {
    const missing = evidence.filter((f) => f.status === "missing").length;
    const present = evidence.filter((f) => f.status === "present").length;
    const neutral = evidence.filter((f) => f.status === "neutral").length;
    return missing > 0 ? { variant: "outstanding", text: `${missing} missing` }
        : present > 0 && neutral === 0 ? { variant: "confirmed", text: "All confirmed" }
        : { variant: "neutral", text: "Details" };
};

export const CRMRoadblockEvidenceSummaryToggle = (props: {
    evidence: Array<TRoadblockEvidenceFact>;
    isOpen: boolean;
    onToggle: () => void;
}): JSX.Element => {
    const summary = summarise(props.evidence);
    return (
        <div
            className={`crm-roadblock-checklist-item-evidence__summary crm-roadblock-checklist-item-evidence__summary--${summary.variant}`}
            onClick={props.onToggle}
        >
            <SpacingRow spacing={CRMSpacing.TINY} alignItems="center">
                <WeightSemiBold>{summary.text}</WeightSemiBold>
                <CRMIcon
                    iconName={props.isOpen ? "arrow-up" : "tooltip-open"}
                    size="tiny"
                    colour="neutral-ink"
                    inline={true}
                />
            </SpacingRow>
        </div>
    );
};

export const CRMRoadblockEvidenceFacts = (props: {
    evidence: Array<TRoadblockEvidenceFact>;
    onOpenEvidenceLink: (link: TRoadblockEvidenceLink) => void;
}): JSX.Element =>
    <Background padding="8px" color={CRMColors.HIGHLIGHTS_CALMING_PINK_12} borderRadius="5px">
        <SpacingColumn spacing={CRMSpacing.TINY}>
            {props.evidence.map((evidenceFact, index) => {
                const statusIcon = factStatusIcon(evidenceFact.status);
                const link = evidenceFact.link;
                return (
                    <SpacingRow
                        key={`${evidenceFact.label}_${index}`}
                        spacing={CRMSpacing.TINY}
                        alignItems="flex-start"
                        childSize="14px 1fr auto"
                    >
                        <CRMIcon
                            iconName={statusIcon.iconName}
                            size="tiny"
                            colour="hex"
                            hexColour={statusIcon.colour}
                        />
                        <CRMParagraph>{evidenceFact.label}</CRMParagraph>
                        {link !== null
                            ? <div
                                className="crm-roadblock-checklist-item-evidence__link"
                                onClick={() => props.onOpenEvidenceLink(link)}
                            >
                                <CRMParagraph>{evidenceFact.value}</CRMParagraph>
                            </div>
                            : <CRMParagraph>{evidenceFact.value}</CRMParagraph>
                        }
                    </SpacingRow>
                );
            })}
        </SpacingColumn>
    </Background>;
